import { ReactElement, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { IonButton, IonModal, IonIcon } from "@ionic/react";
import { checkmark } from "ionicons/icons";
import LanguageLabel from "./LanguageLabel";
import styled from "styled-components";
import { useConfig } from "../../../../contexts/ConfigContext";
import { getStudyLangs } from "../../../../services/study.service";
import {
  ModalOverlay,
  ModalCard,
  ModalTitle,
  ModalActions,
  ModalQuietButton,
} from "../../../atoms/ModalKit";

interface LanguageSelectorModalProps {
  modalVisible: boolean;
  onCloseClick: () => void;
}

const LanguageSelectorModal = ({
  modalVisible,
  onCloseClick,
}: LanguageSelectorModalProps): ReactElement => {
  const { i18n, t } = useTranslation();
  const { study } = useConfig();
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [langs, setLangs] = useState<string[]>([]);

  useEffect(function () {
    loadLangs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function loadLangs() {
    const { data } = await getStudyLangs(study.id);
    setLangs([data.primary_lang, ...data.additional_langs]);
  }

  const handleItemClick = (lang: string): void => {
    i18n.changeLanguage(lang);
    localStorage.setItem('brows_lang', lang);
    onCloseClick();
  };

  const renderLang = (lang: string): ReactElement => {
    return (
      <Language key={lang} onClick={(): void => handleItemClick(lang)}>
        <LanguageLabel lang={lang} />
        {i18n.language === lang && (
          <IonButton slot="end" fill="clear">
            <CheckIcon
              slot="icon-only"
              icon={checkmark}
              size="medium"
            />
          </IonButton>
        )}
      </Language>
    );
  };

  return (
    <IonModal isOpen={modalVisible}>
      <ModalOverlay>
        <ModalCard>
          <ModalTitle>{t("menu_languages_modal_title")}</ModalTitle>
          {langs?.map(renderLang)}
          <ModalActions>
            <ModalQuietButton onClick={onCloseClick}>{t("common_cancel")}</ModalQuietButton>
          </ModalActions>
        </ModalCard>
      </ModalOverlay>
    </IonModal>
  );
};

const Language = styled.div`
  display: flex;
  width: 100%;
  justify-content: space-between;
  padding: 5px 0;
  height: 40px;
  align-items: center;
  color: var(--app-color-text, #041723);
  cursor: pointer;

  & + & {
    border-top: 1px solid rgba(var(--app-color-text-rgb, 4, 23, 35), 0.08);
  }
`;

/* Active-language mark — link/active ink comes from the accent token. */
const CheckIcon = styled(IonIcon)`
  color: var(--app-color-accent, #4285f4);
`;

export default LanguageSelectorModal;
